import React, { useState, useEffect, useCallback } from 'react'
import { connect } from 'react-redux'
import { Api, Endpoint } from '../../api'
import { toast } from 'react-toastify'
import { Button, Form } from 'react-bootstrap'
import { store } from '../../store-redux/connect/store'
import { RESTAURANT_FETCHED_SUCCESSFULLY, methods as restaurantActions } from '../../store-redux/actions/restaurant'

const SquareIntegration = ({ Restaurant, onStatusChange }) => {
	const [connecting, setConnecting] = useState(false)
	const [disconnecting, setDisconnecting] = useState(false)
	const [togglingAcceptOrders, setTogglingAcceptOrders] = useState(false)
	const [status, setStatus] = useState(null)
	const [orderingScopeStatus, setOrderingScopeStatus] = useState(null)
	const { restaurant } = Restaurant

	// Notify parent when status changes
	useEffect(() => {
		if (onStatusChange) {
			onStatusChange(status)
		}
	}, [status, onStatusChange])

	const fetchOrderingScopeStatus = useCallback(async () => {
		try {
			const result = await Api.call(Endpoint.square.orderingScopes)
			if (result?.confirmation === 'Success' && result?.message) {
				setOrderingScopeStatus(result.message)
			} else {
				setOrderingScopeStatus(null)
			}
		} catch (error) {
			console.error('Error fetching Square ordering scopes:', error)
			setOrderingScopeStatus(null)
		}
	}, [])

	const fetchStatus = useCallback(async () => {
		try {
			const result = await Api.call(Endpoint.square.status)
			if (result?.confirmation === 'Success') {
				const nextStatus = result?.message?.status || null
				setStatus(nextStatus)
				if (nextStatus?.isConnected) {
					await fetchOrderingScopeStatus()
				} else {
					setOrderingScopeStatus(null)
				}
			} else {
				setStatus(null)
				setOrderingScopeStatus(null)
			}
		} catch (error) {
			console.error('Error fetching Square status:', error)
			setStatus(null)
			setOrderingScopeStatus(null)
		}
	}, [fetchOrderingScopeStatus])

	useEffect(() => {
		fetchStatus()
		restaurantActions.find({ forceRender: true })
	}, [fetchStatus])

	const handleAcceptOrdersToggle = async (event) => {
		const acceptOrders = event.target.checked
		setTogglingAcceptOrders(true)
		try {
			const result = await Api.call(Endpoint.setAcceptOrders, { data: { acceptOrders } })
			if (result?.confirmation === 'Success') {
				store.dispatch({
					type: RESTAURANT_FETCHED_SUCCESSFULLY,
					restaurant: { ...restaurant, ...result.message },
				})
				toast.success(acceptOrders ? 'Now accepting orders' : 'Orders paused')
			} else {
				toast.error(result?.message || 'Failed to update order acceptance setting')
			}
		} catch (error) {
			toast.error('Error updating order acceptance: ' + error.message)
		} finally {
			setTogglingAcceptOrders(false)
		}
	}

	const handleConnect = async () => {
		setConnecting(true)
		try {
			const result = await Api.call(Endpoint.square.authorize)

			console.log(result)

			if (result?.confirmation === 'Success' && result?.message?.authorizationUrl) {
				// Redirect user to Square OAuth page
				window.location.href = result.message.authorizationUrl
			} else {
				toast(result?.message?.message || 'Failed to initiate Square connection')
				setConnecting(false)
			}
		} catch (error) {
			toast('Error connecting to Square: ' + error.message)
			setConnecting(false)
		}
	}

	const handleDisconnect = async () => {
		if (
			!window.confirm(
				'Are you sure you want to disconnect your Square account? You will need to reconnect to import catalog items.'
			)
		) {
			return
		}

		setDisconnecting(true)
		try {
			const result = await Api.call(Endpoint.square.disconnect)

			if (result?.confirmation === 'Success') {
				toast.success('Square account disconnected successfully')
				setStatus(null)
				setOrderingScopeStatus(null)
				await fetchStatus() // Refresh status
			} else {
				toast(result?.message || 'Failed to disconnect Square account')
			}
		} catch (error) {
			toast('Error disconnecting Square: ' + error.message)
		} finally {
			setDisconnecting(false)
		}
	}

	const isConnected = status?.isConnected === true
	const acceptOrders = restaurant?.acceptOrders === true
	const acceptOrdersSetBySyncAt = restaurant?.acceptOrdersSetBySyncAt

	return (
		<div className="square-integration">
			<div className="square-status-card">
				<h3>Square POS Integration</h3>

				{isConnected && orderingScopeStatus && orderingScopeStatus.hasOrderingScopes === false && (
					<div className="ordering-scopes-banner">
						Order & Pay requires updated permissions — reconnect your Square account.
					</div>
				)}

				{/* enable when payment-order features are ready */}
				{/* <div className="accept-orders-section">
					<div className="accept-orders-header">
						<div>
							<h4>Accept Orders</h4>
							<p>Pause incoming customer orders when your queue is full or you are closing early.</p>
						</div>
						<Form.Check
							type="switch"
							id="accept-orders-toggle"
							checked={acceptOrders}
							disabled={!isConnected || togglingAcceptOrders}
							onChange={handleAcceptOrdersToggle}
							label={acceptOrders ? 'On' : 'Off'}
						/>
					</div>
					{acceptOrdersSetBySyncAt && (
						<p className="sync-label">
							Last set by Square sync at {new Date(acceptOrdersSetBySyncAt).toLocaleString()}
						</p>
					)}
				</div> */}

				<div className="status-info">
					<div className="status-row">
						<span className="label">Status:</span>
						<span className={`status-badge ${isConnected ? 'connected' : 'disconnected'}`}>
							{isConnected ? 'Connected' : 'Not Connected'}
						</span>
					</div>

					<>
						<div className="status-row">
							<span className="label">Merchant ID:</span>
							<span className="value">{status?.merchantId || '-'}</span>
						</div>

						<div className="status-row">
							<span className="label">Environment:</span>
							<span className="value">{status?.environment || '-'}</span>
						</div>

						{status?.lastSyncAt && (
							<div className="status-row">
								<span className="label">Last Sync:</span>
								<span className="value">{new Date(status.lastSyncAt).toLocaleString()}</span>
							</div>
						)}

						{status?.totalCatalogItems !== undefined && (
							<div className="status-row">
								<span className="label">Catalog Items:</span>
								<span className="value">{status?.totalCatalogItems || '-'}</span>
							</div>
						)}

						{status?.connectionStatus === 'error' && (
							<div className="status-row error">
								<span className="label">Connection Status:</span>
								<span className="value">Error - Please reconnect</span>
							</div>
						)}
					</>
				</div>

				<div className="actions">
					{!isConnected ? (
						<Button disabled={connecting} onClick={handleConnect}>
							Connect Square Account
						</Button>
					) : (
						<Button disabled={disconnecting} variant="danger" onClick={handleDisconnect}>
							Disconnect Square
						</Button>
					)}
				</div>

				{!isConnected && (
					<div className="info-message">
						<p>
							Connect your Square account to automatically import your menu items, categories, and images
							from Square POS.
						</p>
					</div>
				)}
			</div>

			<style jsx="true">{`
				.square-integration {
					max-width: 600px;
					margin: 20px auto;
				}

				.square-status-card {
					background: white;
					border-radius: 8px;
					padding: 24px;
					box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
				}

				.square-status-card h3 {
					margin: 0 0 20px 0;
					font-size: 20px;
					font-weight: 600;
				}

				.ordering-scopes-banner {
					margin: 0 0 20px 0;
					padding: 12px 14px;
					background: #fff3cd;
					border: 1px solid #ffc107;
					border-radius: 6px;
					color: #856404;
					font-size: 14px;
					line-height: 1.45;
				}

				.accept-orders-section {
					margin-bottom: 20px;
					padding-bottom: 20px;
					border-bottom: 1px solid #f0f0f0;
				}

				.accept-orders-header {
					display: flex;
					justify-content: space-between;
					align-items: flex-start;
					gap: 16px;
				}

				.accept-orders-header h4 {
					margin: 0 0 6px 0;
					font-size: 16px;
					font-weight: 600;
				}

				.accept-orders-header p {
					margin: 0;
					color: #666;
					font-size: 14px;
					line-height: 1.45;
					max-width: 420px;
				}

				.sync-label {
					margin: 12px 0 0 0;
					font-size: 13px;
					color: #856404;
				}

				.status-info {
					margin-bottom: 20px;
				}

				.status-row {
					display: flex;
					justify-content: space-between;
					padding: 10px 0;
					border-bottom: 1px solid #f0f0f0;
				}

				.status-row:last-child {
					border-bottom: none;
				}

				.status-row .label {
					font-weight: 500;
					color: #666;
				}

				.status-row .value {
					color: #333;
				}

				.status-badge {
					padding: 4px 12px;
					border-radius: 12px;
					font-size: 14px;
					font-weight: 500;
				}

				.status-badge.connected {
					background: #d4edda;
					color: #155724;
				}

				.status-badge.disconnected {
					background: #f8d7da;
					color: #721c24;
				}

				.status-row.error .value {
					color: #dc3545;
				}

				.actions {
					margin-top: 20px;
				}

				.info-message {
					margin-top: 16px;
					padding: 12px;
					background: #e7f3ff;
					border-left: 4px solid #007bff;
					border-radius: 4px;
				}

				.info-message p {
					margin: 0;
					color: #004085;
					font-size: 14px;
				}

				.square-integration-loading {
					text-align: center;
					padding: 40px;
					color: #666;
				}
			`}</style>
		</div>
	)
}

const mapStateToProps = (state) => ({
	Restaurant: state?.Restaurant,
})

export default connect(mapStateToProps)(SquareIntegration)
